import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetDocumentChecklist } from '../../../services/loanService';
import type { DocumentChecklistItem } from '../../../services/schemas/loanServiceSchemas/documentChecklistItem';
import { requirementProductName, stageLabel } from '../admin/DocumentRequirementsSettings';

const STATUS: Record<string, { label: string; className: string; icon: string }> = {
  COMPLETE: { label: 'Complete', className: 'bg-success-subtle text-success', icon: 'fa-check-circle' },
  PARTIAL: { label: 'Gate met · balance due', className: 'bg-info-subtle text-info-emphasis', icon: 'fa-adjust' },
  MISSING: { label: 'Missing', className: 'bg-danger-subtle text-danger', icon: 'fa-times-circle' },
  OPTIONAL: { label: 'Optional', className: 'bg-secondary-subtle text-secondary', icon: 'fa-circle-o' },
};

const progress = (item: DocumentChecklistItem) => {
  if (item.systemSatisfied) return 'Captured in the application';
  const total = item.totalExpected ?? 0;
  if (total <= 1) return (item.received ?? 0) >= 1 ? 'Received' : 'Not received';
  const minimum = item.minimumAtGate ?? 0;
  return `${item.received ?? 0} of ${total}${minimum && minimum < total ? ` · ${minimum} release ${stageLabel(item.blockingStage).toLowerCase()}` : ''}`;
};

/**
 * The application's checklist from Configure Requirements: what its product needs, how much is in, and whether its
 * documents allow each stage. {@code refreshKey} changes whenever documents are added or removed.
 */
const DocumentChecklistPanel: React.FC<{ applicationId: string; refreshKey?: string }> = ({ applicationId, refreshKey }) => {
  const { data, refetch } = useGetDocumentChecklist(applicationId, { query: { enabled: !!applicationId } });
  const [open, setOpen] = useState(false);
  useEffect(() => { if (refreshKey !== undefined) refetch(); }, [refreshKey, refetch]);

  const items = data?.items || [];
  if (!data || items.length === 0) return null;
  const stages = data.stages || [];
  const compulsory = items.filter(i => i.compulsory);
  const done = compulsory.filter(i => i.status === 'COMPLETE').length;
  const nextBlocked = stages.find(s => !s.ready);

  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <h5 className="fw-bold mb-1">Document checklist &amp; stage gates</h5>
            <small className="text-muted">
              {requirementProductName(data.fundingProduct)}{data.secured ? ' · secured' : ''} · {done} of {compulsory.length} compulsory documents complete.
              {' '}Set in <Link to="/settings/document-requirements">Configure Requirements</Link>.
            </small>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? 'Hide checklist' : 'Show checklist'}
          </button>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3">
          {stages.map(s => (
            <span key={s.stage} className={`badge rounded-pill px-3 py-2 ${s.ready ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning-emphasis'}`}
              title={s.ready ? 'Documents allow this stage' : `Held by: ${(s.blockingItems || []).join(', ')}`}>
              <i className={`fa ${s.ready ? 'fa-unlock' : 'fa-lock'} me-1`}></i>
              {stageLabel(s.stage)}{!s.ready && ` · ${(s.blockingItems || []).length} outstanding`}
            </span>
          ))}
        </div>
        {nextBlocked && (
          <div className="small text-warning-emphasis mt-2">
            <i className="fa fa-exclamation-triangle me-1"></i>
            {stageLabel(nextBlocked.stage)} needs: {(nextBlocked.blockingItems || []).join(', ')}
          </div>
        )}

        {open && (
          <div className="table-responsive mt-3">
            <table className="table table-sm align-middle mb-0">
              <thead className="small text-muted">
                <tr><th>Document</th><th>Progress</th><th>Needed by</th><th>Status</th></tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const status = STATUS[item.status || ''] || STATUS.OPTIONAL;
                  return (
                    <tr key={`${item.ruleId}-${item.directorId || ''}`}>
                      <td>
                        <div className="small fw-semibold">{item.documentName}</div>
                        <div className="small text-muted">{item.ruleId}{item.documentGroup ? ` · ${item.documentGroup}` : ''}{item.compulsory ? '' : ' · optional'}</div>
                      </td>
                      <td className="small">{progress(item)}</td>
                      <td className="small">
                        {stageLabel(item.blockingStage)}
                        {item.fullCompletionStage && item.fullCompletionStage !== item.blockingStage && (item.totalExpected ?? 0) > (item.minimumAtGate ?? 0) && (
                          <div className="text-muted">All by {stageLabel(item.fullCompletionStage).toLowerCase()}</div>
                        )}
                      </td>
                      <td><span className={`badge ${status.className}`}><i className={`fa ${status.icon} me-1`}></i>{status.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentChecklistPanel;
